import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const Contact = () => {
  const headerRef = useRef(null);
  const formRef = useRef(null);
  const infoRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    dogInfo: '',
    message: ''
  });

  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  useEffect(() => {
    // Animation sobre d'entrée de la page
    const tl = gsap.timeline();
    
    tl.fromTo(headerRef.current.querySelector('h1'),
      { opacity: 0 },
      { opacity: 1, duration: 0.8, ease: "power2.out" }
    )
    .fromTo(headerRef.current.querySelector('.lead'),
      { opacity: 0 },
      { opacity: 1, duration: 0.6, ease: "power2.out" },
      "-=0.4"
    );

    // Animation sobre du formulaire
    gsap.fromTo(formRef.current.querySelectorAll('.form-group'),
      { opacity: 0 },
      {
        opacity: 1,
        duration: 0.6,
        stagger: 0.1,
        ease: "power2.out",
        scrollTrigger: {
          trigger: formRef.current,
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: "play none none reverse"
        }
      }
    );

    // Animation sobre des informations de contact
    gsap.fromTo(infoRef.current.querySelectorAll('.contact-info-item'),
      { opacity: 0 },
      {
        opacity: 1,
        duration: 0.6,
        stagger: 0.15,
        ease: "power2.out",
        scrollTrigger: {
          trigger: infoRef.current,
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: "play none none reverse"
        }
      }
    );

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          service: formData.service,
          message: `Service demandé: ${formData.service || 'Non spécifié'}\n\nInformations sur le chien: ${formData.dogInfo || 'Non renseignées'}\n\nMessage: ${formData.message}`
        }),
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          dogInfo: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {

      title: 'Téléphone',
      content: '06 67 45 50 59',
      description: '7 jours sur 7'
    },
    {

      title: 'Email',
      content: 'contact@atoutcanin.fr',
      description: 'Réponse sous 24h'
    },
    {
      title: 'Adresse',
      content: '3 rue Salvador Allende 69600 Oullins',
      description: 'Sur rendez-vous'
    },
    {

      title: 'Instagram',
      content: 'atout_can1',
      description: 'Suivez-moi sur Instagram'
    }
  ];

  const services = [
    { value: 'bilan', label: 'Bilan comportemental' },
    { value: 'education', label: 'Séances d\'éducation' },
    { value: 'reeducation', label: 'Rééducation comportementale' },
    { value: 'balades', label: 'Balades collectives' },
    { value: 'detection', label: 'Ateliers détection d\'odeurs' },
    { value: 'tricks', label: 'Tricks funs' }
  ];

  return (
    <div className="contact-page">
      {/* Header */}
      <section ref={headerRef} className="hero">
        <div className="container">
          <div className="hero-content">
            <h1>Contactez-Moi</h1>
            <p className="lead">
              Prêt à commencer l'éducation de votre chien ?<br />
              Contactez-moi pour un devis gratuit et personnalisé
            </p>
          </div>
        </div>
      </section>

      {/* Formulaire et informations */}
      <section className="section">
        <div className="container">
          <div className="grid grid-2">
            {/* Formulaire */}
            <div ref={formRef}>
              <h2>Demander un Devis</h2>
              <p className="mb-lg">
                Remplissez ce formulaire pour obtenir un devis personnalisé. 
                Je vous répondrai dans les plus brefs délais.
              </p>
              
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Nom *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="form-control"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="form-label">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="form-control"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone" className="form-label">Téléphone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="form-control"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="service" className="form-label">Service souhaité</label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    className="form-control"
                  >
                    <option value="">Choisir un service</option>
                    {services.map((service) => (
                      <option key={service.value} value={service.value}>
                        {service.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="dogInfo" className="form-label">Informations sur votre chien</label>
                  <textarea
                    id="dogInfo"
                    name="dogInfo"
                    value={formData.dogInfo}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Âge, race, taille, problèmes comportementaux observés..."
                    rows="3"
                  ></textarea>
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="form-label">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Décrivez votre demande, vos objectifs, ou toute information importante..."
                    required
                    rows="4"
                  ></textarea>
                </div>

                {/* Messages de statut */}
                {submitStatus === 'success' && (
                  <div className="alert alert-success" style={{ 
                    background: '#d4edda', 
                    color: '#155724', 
                    padding: 'var(--spacing-sm)', 
                    borderRadius: 'var(--radius-sm)', 
                    marginBottom: 'var(--spacing-md)',
                    border: '1px solid #c3e6cb'
                  }}>
                    ✅ Votre message a été envoyé avec succès ! Vous recevrez une confirmation par email.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="alert alert-error" style={{ 
                    background: '#f8d7da', 
                    color: '#721c24', 
                    padding: 'var(--spacing-sm)', 
                    borderRadius: 'var(--radius-sm)', 
                    marginBottom: 'var(--spacing-md)',
                    border: '1px solid #f5c6cb'
                  }}>
                    ❌ Une erreur est survenue lors de l'envoi. Veuillez réessayer.
                  </div>
                )}

                <button 
                  type="submit" 
                  className="btn btn-primary btn-large" 
                  style={{ width: '100%' }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Envoi en cours...' : 'Envoyer ma demande'}
                </button>
              </form>
            </div>

            {/* Informations de contact */}
            <div ref={infoRef}>
              <div className="contact-info">
                <h3>Informations de Contact</h3>
                <div className="contact-info-grid">
                  {contactInfo.map((info, index) => (
                    <div key={index} className="contact-info-item">
                      <div className="content">
                        <h4>{info.title}</h4>
                        <p>{info.content}</p>
                        <small>{info.description}</small>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="contact-additional">
                  <h4>Modalités Pratiques</h4>
                  <ul>
                    <li><strong>Paiement :</strong> Espèces, chèque ou virement bancaire lors du premier rendez-vous</li>
                    <li><strong>Échelonnement :</strong> Possible sur demande</li>
                    <li><strong>Annulation :</strong> Préavis de 24h minimum</li>
                    <li><strong>Forfaits :</strong> Validité de 4 mois</li>
                  </ul>
                  
                  <h4 className="mt-md">Zone d'Intervention</h4>
                  <p>
                    J'interviens principalement dans la région lyonnaise et l'ouest Lyonnais. 
                    N'hésitez pas à me contacter pour vérifier si je couvre votre secteur.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ rapide */}
      <section className="section section-alt">
        <div className="container">
          <div className="section-header">
            <h2>Questions Fréquentes</h2>
            <p className="lead">
              Les réponses aux questions les plus courantes
            </p>
          </div>
          
          <div className="faq-accordion" style={{ maxWidth: '800px', margin: '0 auto' }}>
            {[
              {
                question: "Combien de temps dure une séance ?",
                answer: "Les séances d'éducation durent généralement 1 heure. Le bilan comportemental peut prendre entre 1h30 et 2h selon les besoins."
              },
              {
                question: "À partir de quel âge ?",
                answer: "J'accepte les chiens à partir de 3 mois, mais il n'y a pas d'âge limite supérieur. Chaque chien peut apprendre !"
              },
              {
                question: "Combien de séances sont nécessaires ?",
                answer: "Cela dépend des objectifs et du chien. En moyenne, 3 à 5 séances suffisent pour voir des améliorations significatives."
              },
              {
                question: "Que faire en cas d'urgence ?",
                answer: "Pour les urgences comportementales, contactez-moi par téléphone. Je fais mon possible pour répondre rapidement."
              }
            ].map((faq, index) => (
              <div key={index} className="faq-item">
                <button 
                  className={`faq-question ${openFaq === index ? 'active' : ''}`}
                  onClick={() => toggleFaq(index)}
                >
                  <span>{faq.question}</span>
                  <span className="faq-icon">
                    {openFaq === index ? '−' : '+'}
                  </span>
                </button>
                <div className={`faq-answer ${openFaq === index ? 'open' : ''}`}>
                  <div className="faq-content">
                    <p>{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <p>&copy; 2024 Atout Canin - Éducation et Rééducation Canine. Tous droits réservés.</p>
          <p>
            <Link to="/cgv">Conditions Générales de Vente</Link>
            <Link to="/contact">Mentions Légales</Link>
          </p>
        </div>
      </footer>

      <style jsx>{`
        .contact-info-grid {
          display: grid;
          gap: var(--spacing-md);
          margin-bottom: var(--spacing-lg);
        }
        
        .contact-additional {
          background: var(--light-gray);
          padding: var(--spacing-md);
          border-radius: var(--radius-md);
          margin-top: var(--spacing-lg);
        }
        
        .contact-additional h4 {
          color: var(--primary-color);
          margin-bottom: var(--spacing-sm);
        }
        
        .contact-additional ul {
          margin-bottom: var(--spacing-md);
        }
        
        .contact-additional li {
          margin-bottom: var(--spacing-xs);
        }
        
        .contact-additional small {
          color: var(--text-muted);
          font-size: 0.9rem;
        }
      `}</style>
    </div>
  );
};

export default Contact; 